import { Centrifuge, UnauthorizedError } from 'centrifuge'

/**
 * Browser transport for realtime push over Centrifugo (centrifuge-js v5).
 * Contract: docs/superpowers/specs/2026-10-21-centrifugo-realtime-design.md
 * (§4 channels, §5 token, §6 tabs, §7 scanner views, §9 this layer).
 *
 * One client per tab (`getRealtimeClient()` in `realtime-service.ts`). The
 * `Centrifuge` instance is created on the first listener, connects lazily and
 * is torn down 5 s after the last listener leaves; the next listener builds a
 * new one. This module has no app imports so it unit-tests against a fake
 * Centrifuge.
 *
 * Channels (caller names):
 *   `ticker:<SYM>`              chart bars          → {ch: 'ticker:<SYM>', type: 'bar', data}
 *   `user:#`  (onMessageType)   price alerts, alert-window matches; the
 *                               publication `{type, data, ts}` is unwrapped
 *                               → {ch: 'user', type, data, ts}
 *   `sv:<nonce>_<viewId>#`      one scanner view    → {ch, type: 'scanner_view', data}
 * A caller channel ending in `#` is user-limited: the transport appends the
 * signed-in user's edgeUserId, learned from the token mint (`user`).
 *
 * Subscriptions are ref-counted: one Centrifuge `Subscription` per channel
 * (`getSubscription` before `newSubscription`, which throws on duplicates);
 * the last release unsubscribes and calls `removeSubscription`. Recovery on
 * `user:` comes from the namespace's `force_recovery`; publications replayed
 * by it arrive with `recovered: true`.
 */

export interface RealtimeMessage {
  ch: string
  type: string
  data: unknown
  ts: number
  /** Replayed by history recovery after a reconnect, not live. */
  recovered?: boolean
}

export type RealtimeListener = (msg: RealtimeMessage) => void

/** The (re)subscribe outcome of one channel (Centrifuge `subscribed`). */
export interface RealtimeSubscribedContext {
  /** A resubscribe that asked for the missed publications. */
  wasRecovering: boolean
  /** Every missed publication was replayed (false after a gap too long to recover). */
  recovered: boolean
}

export interface RealtimeSubscriptionEvents {
  /** Every (re)subscribe, including the first. */
  onSubscribed?: (ctx: RealtimeSubscribedContext) => void
  /**
   * The subscription is (re)subscribing, or its Centrifuge subscription was
   * torn down (logout, user change) while the listener stays registered: no
   * publications until the next onSubscribed.
   */
  onSubscribing?: () => void
  /**
   * Centrifugo refused the subscription for good (a non-temporary subscribe
   * error, or a server unsubscribe): the SDK will not retry it, so no
   * onSubscribed follows on this connection.
   */
  onUnsubscribed?: (ctx: { code: number; reason: string }) => void
}

/** `POST /v2/realtime/token` (§5): an HS256 connection token, the user it names, its expiry. */
export interface RealtimeToken {
  token: string
  /** edgeUserId: the `#<user>` of user-limited channels. */
  user: string
  /** Unix seconds. */
  exp: number
}

// ─── The slice of centrifuge-js the client uses (a fake in tests) ───

export interface SubscribedContextLike {
  wasRecovering: boolean
  recovered: boolean
  streamPosition?: { offset: number; epoch: string }
}

export interface PublicationContextLike {
  channel: string
  data: unknown
  offset?: number
}

export interface SubscriptionLike {
  readonly channel: string
  readonly state: string
  on(event: 'publication', fn: (ctx: PublicationContextLike) => void): unknown
  on(event: 'subscribed', fn: (ctx: SubscribedContextLike) => void): unknown
  on(event: 'subscribing', fn: (ctx: unknown) => void): unknown
  on(event: 'unsubscribed', fn: (ctx: { code: number; reason: string }) => void): unknown
  subscribe(): void
  unsubscribe(): void
}

export interface CentrifugeLike {
  readonly state: string
  connect(): void
  disconnect(): void
  newSubscription(channel: string): SubscriptionLike
  getSubscription(channel: string): SubscriptionLike | null
  removeSubscription(sub: SubscriptionLike | null): void
  rpc(method: string, data: unknown): Promise<{ data: unknown }>
  removeAllListeners?(): unknown
}

export interface CentrifugeFactoryOptions {
  getToken: () => Promise<string>
}

export type CentrifugeFactory = (url: string, options: CentrifugeFactoryOptions) => CentrifugeLike

const defaultCentrifuge: CentrifugeFactory = (url, { getToken }) =>
  new Centrifuge(url, { getToken }) as unknown as CentrifugeLike

export { UnauthorizedError }

/**
 * A per-tab nonce (§6): 12 hex characters from `crypto.randomUUID()`. Never
 * kept in sessionStorage (a duplicated tab would copy it and share a conn).
 */
export function newTabNonce(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 12)
}

export interface RealtimeClientOptions {
  /** Full ws:/wss: URL of Centrifugo's `/connection/websocket`, read per instance. */
  url: () => string
  /**
   * Mint a connection token (§5). Throw `UnauthorizedError` when no token can
   * come (no session, 401 after refresh, realtime not configured): the SDK
   * stops. Any other error is retried by the SDK with backoff.
   */
  mintToken: () => Promise<RealtimeToken>
  createCentrifuge?: CentrifugeFactory
  /** The tab nonce source; called at construction and after every reset(). */
  nonce?: () => string
  /** Keep the connection this long after the last listener leaves. Default 5 s. */
  idleCloseMs?: number
}

const USER_CHANNEL = 'user:#'

interface Entry {
  key: string
  listeners: Set<RealtimeListener>
  events: Set<RealtimeSubscriptionEvents>
  sub: SubscriptionLike | null
  /** Offsets at or below this were replayed by the last recovering subscribe. */
  recoveredTop: number | null
}

function namespaceOf(channel: string): string {
  const i = channel.indexOf(':')
  return i < 0 ? '' : channel.slice(0, i)
}

export class RealtimeClient {
  private readonly opts: Required<Omit<RealtimeClientOptions, 'nonce'>> & {
    nonce: () => string
  }
  private readonly entries = new Map<string, Entry>()
  private readonly types = new Map<string, Set<RealtimeListener>>()

  private centrifuge: CentrifugeLike | null = null
  private user: string | null = null
  private disposed = false
  private idleTimer: ReturnType<typeof setTimeout> | null = null
  private _nonce: string
  /** Releases the `user:#` entry once no type listener is left. */
  private releaseUserChannel: (() => void) | null = null

  constructor(options: RealtimeClientOptions) {
    this.opts = {
      createCentrifuge: defaultCentrifuge,
      idleCloseMs: 5_000,
      nonce: newTabNonce,
      ...options,
    }
    this._nonce = this.opts.nonce()
  }

  /** This tab's nonce (§6), for `sv:<nonce>_<viewId>#` channels and the rpc `tab`. */
  get nonce(): string {
    return this._nonce
  }

  /**
   * Listen to one channel. Ref-counted: one Centrifuge subscription however
   * many listeners share it, removed when the last one leaves. `events` see
   * the subscription's (re)subscribes; a listener joining an already
   * subscribed channel gets one `onSubscribed` right away (next microtask).
   * Returns the unsubscribe function (idempotent).
   */
  subscribe(
    channel: string,
    listener: RealtimeListener,
    events?: RealtimeSubscriptionEvents,
  ): () => void {
    let entry = this.entries.get(channel)
    const isNew = !entry
    if (!entry) {
      entry = {
        key: channel,
        listeners: new Set(),
        events: new Set(),
        sub: null,
        recoveredTop: null,
      }
      this.entries.set(channel, entry)
    }
    entry.listeners.add(listener)
    if (events) entry.events.add(events)
    this.retain()
    if (isNew) this.bind(entry)
    else if (events?.onSubscribed && entry.sub?.state === 'subscribed') {
      const e = entry
      queueMicrotask(() => {
        if (
          this.entries.get(channel) === e &&
          e.events.has(events) &&
          e.sub?.state === 'subscribed'
        ) {
          events.onSubscribed?.({ wasRecovering: false, recovered: false })
        }
      })
    }

    let done = false
    return () => {
      if (done) return
      done = true
      const current = this.entries.get(channel)
      if (!current) return
      current.listeners.delete(listener)
      if (events) current.events.delete(events)
      if (current.listeners.size === 0) {
        this.entries.delete(channel)
        this.unbind(current)
      }
      this.release()
    }
  }

  /**
   * Listen to every `user:#<me>` publication of one `type` (§9). `events`
   * see the user channel's (re)subscribes, e.g. `recovered: false` after an
   * outage longer than its history.
   */
  onMessageType(
    type: string,
    listener: RealtimeListener,
    events?: RealtimeSubscriptionEvents,
  ): () => void {
    let set = this.types.get(type)
    if (!set) {
      set = new Set()
      this.types.set(type, set)
    }
    set.add(listener)
    const offEvents = events ? this.subscribe(USER_CHANNEL, () => {}, events) : null
    this.releaseUserChannel ??= this.subscribe(USER_CHANNEL, (msg) => this.dispatchType(msg))

    let done = false
    return () => {
      if (done) return
      done = true
      offEvents?.()
      const current = this.types.get(type)
      if (current) {
        current.delete(listener)
        if (current.size === 0) this.types.delete(type)
      }
      if (this.types.size === 0 && this.releaseUserChannel) {
        const off = this.releaseUserChannel
        this.releaseUserChannel = null
        off()
      }
    }
  }

  /**
   * One RPC through Centrifugo (§7: `scanner:view` goes to the scanner's RPC
   * proxy). Resolves with the reply's `data`; rejects on a proxy or transport
   * failure, including the SDK's timeout while disconnected. Callers must
   * catch.
   */
  async rpc(method: string, data: Record<string, unknown>): Promise<unknown> {
    const c = this.centrifuge
    if (!c) throw new Error('realtime: not connected')
    const res = await c.rpc(method, data)
    return res?.data
  }

  /**
   * Logout (§9, bug 1): drop the connection, the user and the nonce now.
   * Listeners stay registered and quiet; `resume()` (the next session)
   * builds a NEW Centrifuge, mints a new token and re-subscribes them.
   */
  reset(): void {
    this.teardown()
    this._nonce = this.opts.nonce()
  }

  /** A session is back: reconnect if anything still listens. */
  resume(): void {
    if (this.disposed || this.entries.size === 0) return
    this.ensureConnected()
  }

  /** Close for good (tests). Listeners stay registered but go quiet. */
  dispose(): void {
    this.disposed = true
    this.teardown()
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  private retain(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer)
      this.idleTimer = null
    }
    this.ensureConnected()
  }

  private release(): void {
    if (this.entries.size > 0 || this.idleTimer) return
    this.idleTimer = setTimeout(() => {
      this.idleTimer = null
      if (this.entries.size > 0) return
      this.teardown()
    }, this.opts.idleCloseMs)
  }

  private ensureConnected(): void {
    if (this.disposed) return
    // An instance the SDK stopped (UnauthorizedError) never retries by
    // itself: a new listener replaces it, so realtime can come back.
    if (this.centrifuge?.state === 'disconnected') this.teardown()
    if (this.centrifuge) return
    const c = this.opts.createCentrifuge(this.opts.url(), {
      getToken: () => this.getToken(c),
    })
    this.centrifuge = c
    for (const entry of this.entries.values()) this.bind(entry)
    c.connect()
  }

  /** The SDK's getToken (§5): mint, learn the user, never return ''. */
  private async getToken(c: CentrifugeLike): Promise<string> {
    const minted = await this.opts.mintToken()
    if (!minted?.token) throw new UnauthorizedError('realtime: empty token')
    if (this.centrifuge === c) this.setUser(minted.user)
    return minted.token
  }

  private setUser(user: string): void {
    if (!user || user === this.user) return
    const previous = this.user
    this.user = user
    for (const entry of this.entries.values()) {
      if (!entry.key.endsWith('#')) continue
      if (previous !== null) this.unbind(entry)
      this.bind(entry)
    }
  }

  private teardown(): void {
    if (this.idleTimer) clearTimeout(this.idleTimer)
    this.idleTimer = null
    const c = this.centrifuge
    for (const entry of this.entries.values()) this.unbind(entry)
    this.centrifuge = null
    this.user = null
    if (c) {
      try {
        c.disconnect()
        c.removeAllListeners?.()
      } catch {
        // already gone
      }
    }
  }

  // ─── Subscriptions ─────────────────────────────────────────────────────────

  private fullChannel(key: string): string | null {
    if (!key.endsWith('#')) return key
    return this.user ? `${key}${this.user}` : null
  }

  private bind(entry: Entry): void {
    const c = this.centrifuge
    if (!c || entry.sub) return
    const channel = this.fullChannel(entry.key)
    if (!channel) return // user-limited: bound once the mint names the user
    const sub = c.getSubscription(channel) ?? c.newSubscription(channel)
    entry.sub = sub
    entry.recoveredTop = null
    sub.on('subscribing', () => {
      if (entry.sub !== sub) return
      for (const e of [...entry.events]) safe(() => e.onSubscribing?.())
    })
    sub.on('subscribed', (ctx) => {
      if (entry.sub !== sub) return
      entry.recoveredTop = ctx.wasRecovering ? (ctx.streamPosition?.offset ?? null) : null
      const out = { wasRecovering: ctx.wasRecovering, recovered: ctx.recovered }
      for (const e of [...entry.events]) safe(() => e.onSubscribed?.(out))
    })
    sub.on('unsubscribed', (ctx) => {
      // Our own unbind clears entry.sub first: only the server's refusal gets here.
      if (entry.sub !== sub) return
      const out = { code: ctx?.code ?? 0, reason: ctx?.reason ?? '' }
      for (const e of [...entry.events]) safe(() => e.onUnsubscribed?.(out))
    })
    sub.on('publication', (ctx) => {
      if (entry.sub !== sub) return
      const recovered =
        entry.recoveredTop !== null && ctx.offset !== undefined && ctx.offset <= entry.recoveredTop
      const msg = this.toMessage(entry.key, ctx.data, recovered)
      for (const l of [...entry.listeners]) safe(() => l(msg))
    })
    sub.subscribe()
  }

  private unbind(entry: Entry): void {
    const sub = entry.sub
    entry.sub = null
    entry.recoveredTop = null
    if (!sub) return
    try {
      sub.unsubscribe()
      this.centrifuge?.removeSubscription(sub)
    } catch {
      // the instance is going away anyway
    }
    // Listeners that stay registered (logout, user change) are no longer
    // subscribed: they must wait for the next onSubscribed.
    for (const e of [...entry.events]) safe(() => e.onSubscribing?.())
  }

  /** §9: rebuild the gateway-era message shape consumers read. */
  private toMessage(key: string, data: unknown, recovered: boolean): RealtimeMessage {
    const ns = namespaceOf(key)
    if (ns === 'user') {
      const d = (data ?? {}) as { type?: unknown; data?: unknown; ts?: unknown }
      return {
        ch: 'user',
        type: typeof d.type === 'string' ? d.type : '',
        data: d.data,
        ts: typeof d.ts === 'number' ? d.ts : 0,
        recovered,
      }
    }
    const type = ns === 'ticker' ? 'bar' : ns === 'sv' ? 'scanner_view' : ns
    return { ch: key, type, data, ts: 0, recovered }
  }

  private dispatchType(msg: RealtimeMessage): void {
    for (const l of [...(this.types.get(msg.type) ?? [])]) safe(() => l(msg))
  }
}

function safe(fn: () => void): void {
  try {
    fn()
  } catch {
    // one bad listener must not starve the others
  }
}
